import { Heading } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { useBotAnalyticsCRUDActions } from '@mm/state/actions/BotAnalyticsCRUDActions';
import { TopDialogue } from '@mm/state/interfaces/botAnalytics';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectTopDialogues } from '@mm/state/selectors/botAnalyticsSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import classnames from 'classnames';
import { Moment } from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import {
  Grid,
  GridHeader,
  GridLabels,
  GridRow,
} from '../../../../../../../common/components/Grid/Grid';
import { PrimaryToolTip } from '../../../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { TableLabel } from '../../../../../../../common/components/TableGrid/TableGrid';
import { BUILD_DIALOGUE } from '../../../../../../../common/constants/routes';
import { useInfiniteScroll } from '../../../../../../../common/hooks/use-infinite-scroll';
import { ComponentDataItem } from '../../../../../contacts/components/ContactsTable/ContactTable';
import {
  renderContactsReachedAmountCell,
  renderConversationSessionsCell,
  renderDialogueNameCell,
  renderRespondedCell,
  renderResponseRateCell,
  renderSentAmountCell,
} from './renderCells';
import './TopDialogues.scss';

type Props = {
  startDate: Moment | null;
  endDate: Moment | null;
  dateRangeMode: string | null;
};

type CellType = ComponentDataItem & {
  explanation?: string;
};

const cells: CellType[] = [
  {
    label: 'Dialogue Name',
    keyName: 'dialogue_name',
    Cell: renderDialogueNameCell,
  },
  {
    label: 'Contacts Reached',
    keyName: 'contacts_reached',
    Cell: renderContactsReachedAmountCell,
    explanation: 'Number of users dialogue was sent to',
  },
  {
    label: 'Sent',
    keyName: 'sent',
    Cell: renderSentAmountCell,
    explanation: 'Number of times this dialogue was sent',
  },
  {
    label: 'Conversation Sessions',
    keyName: 'conversation_sessions',
    Cell: renderConversationSessionsCell,
    explanation:
      'Number of conversation sessions tracked. Data collected from 7/10/2020',
  },
  {
    label: 'Responded',
    keyName: 'responded',
    Cell: renderRespondedCell,
    explanation: 'Number of times a user responded to each dialogue',
  },
  {
    label: 'Response Rate',
    keyName: 'response_rate',
    Cell: renderResponseRateCell,
    explanation:
      'Number of responses divided by number of times a dialogue was sent',
  },
];
const gridTemplateColumns = `35% 15% 10% 15% 15% 10%`;

const CellLabel = (props: { dataItem: CellType }) => {
  const { dataItem } = props;
  return dataItem.explanation ? (
    <PrimaryToolTip
      useHover
      position="top"
      align="end"
      classNameContent="top-dialogues-grid__labels__info-tooltip"
      classNameContainer="top-dialogues-grid__labels__info-tooltip-container"
      classNameArrow="top-dialogues-grid__labels__info-tooltip-arrow"
      anchorElement={
        <TableLabel
          key={dataItem.keyName ? String(dataItem.keyName) : dataItem.label}
          dataItem={dataItem}
        />
      }
    >
      <span>{dataItem.explanation}</span>
    </PrimaryToolTip>
  ) : (
    <TableLabel
      key={dataItem.keyName ? String(dataItem.keyName) : dataItem.label}
      dataItem={dataItem}
    />
  );
};

export const TopDialogues = (props: Props) => {
  const { startDate, endDate, dateRangeMode } = props;
  const { history } = useRouter();
  const [isViewAllMode, setIsViewAllMode] = useState(false);
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const topDialogues = useSelector(selectTopDialogues);
  const { dialogues, pagination } = topDialogues;
  const { retrieveTopDialogues } = useBotAnalyticsCRUDActions();
  const { openUniversalUpgradeLandmineModal } = useModalActions();

  const canViewAllDialogues = useSelector(
    combinedFeatureFlagsSelector,
  ).analyticsBotViewAllDialogues;

  const showUpgradeLandmine = () => {
    openUniversalUpgradeLandmineModal({
      landmineName: landmineNames['BOT-ANALYTICS-TOP-DIALOGUES'],
      feature: 'analyticsBotViewAllDialogues',
    });
  };

  useEffect(() => {
    retrieveTopDialogues(
      activeChatbotId,
      1,
      5,
      startDate?.format('DD-MM-YYYY'),
      endDate?.format('DD-MM-YYYY'),
    );
  }, [startDate, endDate]);

  const fetchMore = () =>
    retrieveTopDialogues(
      activeChatbotId,
      pagination.page + 1,
      pagination.perPage,
      startDate?.format('DD-MM-YYYY'),
      endDate?.format('DD-MM-YYYY'),
    );

  const { scrollContainerRef } = useInfiniteScroll<HTMLDivElement>({
    fetchDisabled: pagination.isLastPage,
    fetchMore,
  });

  const filtredData = useMemo(() => {
    if (isViewAllMode || dialogues.length <= 5) {
      return dialogues;
    }
    return dialogues.slice(0, 5);
  }, [dialogues, isViewAllMode]);

  const onRowClick = (item: TopDialogue, index: number) => {
    if (index !== 0 && !canViewAllDialogues) {
      showUpgradeLandmine();
    } else {
      history.push(BUILD_DIALOGUE(activeChatbotId, item.id));
    }
  };

  const showTeaser = !canViewAllDialogues && filtredData.length > 1;

  const onViewAll = () => {
    if (!showTeaser) {
      setIsViewAllMode(true);
      fetchMore();
    }
  };

  const className = classnames('top-dialogues', {
    'top-dialogues-teaser': showTeaser,
  });

  const containerHeight = scrollContainerRef.current?.clientHeight ?? 0;
  const firstRowHeight =
    scrollContainerRef.current?.firstElementChild?.clientHeight ?? 0;
  const bottomPx = (containerHeight - firstRowHeight) / 2 - 10;

  return (
    <div className={className}>
      <Grid>
        <GridHeader className="top-dialogues__title">
          <Heading text="Top Dialogues" size={3} />
          <div className="top-dialogues__title-date">
            <span className="top-dialogues__title-date-show">Show:</span>{' '}
            {dateRangeMode ? dateRangeMode : 'Custom date range'}
          </div>
        </GridHeader>
        <GridLabels
          className="top-dialogues-grid__labels"
          gridTemplateColumns={gridTemplateColumns}
        >
          {cells.map(dataItem => (
            <CellLabel dataItem={dataItem} key={dataItem.keyName} />
          ))}
        </GridLabels>
        <div
          className="top-dialogues-grid__rows-container"
          ref={scrollContainerRef}
        >
          {filtredData.map((item, index) => (
            <GridRow
              key={item.id}
              gridTemplateColumns={gridTemplateColumns}
              onClick={() => onRowClick(item, index)}
            >
              {cells.map(({ Cell, keyName }) => {
                return (
                  Cell && (
                    <Cell item={item} orderNumber={index + 1} key={keyName} />
                  )
                );
              })}
            </GridRow>
          ))}
          {showTeaser && (
            <div
              className="top-dialogues-grid__upgrade-flag"
              style={{ bottom: `${bottomPx}px` }}
              onClick={showUpgradeLandmine}
            >
              <span>Upgrade</span> to Startup to view all data
            </div>
          )}
        </div>
        {!isViewAllMode && !pagination.isLastPage && (
          <div className="top-dialogues__view-all-button" onClick={onViewAll}>
            View all
          </div>
        )}
      </Grid>
    </div>
  );
};
